import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { baseName } from "@/lib/group";

export default function ProductCard({ p, index = 0, variants }) {
  const { addItem } = useCart();
  const list = variants && variants.length ? variants : [p];
  const grouped = list.length > 1;
  const [sel, setSel] = useState(0);
  const active = list[sel] || p;
  const title = grouped ? baseName(active.name) : active.name;

  return (
    <div className="group bg-ape-surface border border-ape-border relative overflow-hidden fade-up flex flex-col"
      style={{ animationDelay: `${index * 60}ms` }} data-testid={`product-card-${active.slug}`}>
      <div className="h-1 w-full" style={{ backgroundColor: active.accent }} />
      <Link to={`/product/${active.slug}`} className="block relative">
        <div className="aspect-[4/3] bg-gradient-to-b from-[#101010] to-[#1c1c1c] flex items-center justify-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-20" style={{ background: `radial-gradient(circle at 50% 45%, ${active.accent}, transparent 65%)` }} />
          <img src={active.image} alt={title} className="h-full w-full object-contain p-3 relative z-10 group-hover:scale-105 transition-transform duration-300" />
          <span className="absolute top-3 right-3 z-20 font-mono2 text-[10px] tracking-widest text-ape-muted border border-ape-border px-2 py-1 bg-ape-bg/70">
            {active.category.toUpperCase()}
          </span>
        </div>
      </Link>
      <div className="p-6 flex flex-col flex-1">
        <div className="flex items-center gap-2 text-[11px] font-mono2 text-ape-muted mb-2">
          <ShieldCheck size={13} style={{ color: active.accent }} /> BATCH VERIFIED · 99% PURITY
        </div>
        <Link to={`/product/${active.slug}`}>
          <h3 className="font-display text-2xl tracking-wide leading-none group-hover:text-ape-red transition-colors duration-150">{title}</h3>
        </Link>
        <p className="text-ape-muted text-sm mt-2 flex-1">{active.short_desc}</p>

        {grouped && (
          <div className="flex flex-wrap gap-2 mt-4" data-testid={`variant-selector-${p.group || active.slug}`}>
            {list.map((v, i) => (
              <button key={v.slug} onClick={() => setSel(i)} data-testid={`variant-${v.slug}`}
                className={`font-mono2 text-xs tracking-widest px-3 py-1.5 border transition-colors duration-150 ${i === sel ? "bg-ape-red border-ape-red text-white" : "border-ape-border text-ape-muted hover:border-white hover:text-white"}`}>
                {v.dosage}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between mt-5">
          <span className="font-mono2 text-xl font-bold" data-testid={`price-${active.slug}`}>${active.price.toFixed(2)}</span>
          <span className="font-mono2 text-xs text-ape-muted">{active.dosage}</span>
        </div>
        <div className="font-mono2 text-[10px] tracking-widest text-ape-muted mt-2 uppercase">
          99% Purity · Lyophilized Powder · Research Use Only
        </div>
        <button
          onClick={() => { addItem(active); toast.success(`${active.name} added to cart`); }}
          data-testid={`add-to-cart-${active.slug}`}
          className="mt-4 w-full bg-ape-red hover:bg-ape-redhover text-white font-head uppercase tracking-widest text-sm py-3 transition-colors duration-150">
          Add to Cart
        </button>
      </div>
    </div>
  );
}

export function Vial({ accent, label, size = "md" }) {
  const h = size === "lg" ? "h-56" : "h-40";
  const w = size === "lg" ? "w-24" : "w-16";
  return (
    <div className={`relative ${h} ${w} flex flex-col items-center`}>
      <div className="w-3/5 h-3 rounded-sm" style={{ backgroundColor: accent }} />
      <div className="w-3/5 h-2 bg-[#c9c9c9] rounded-sm" />
      <div className="flex-1 w-full bg-gradient-to-b from-[#2a2a2a] to-[#151515] border border-[#3a3a3a] rounded-b-md relative overflow-hidden flex items-end justify-center">
        <div className="absolute inset-x-1 top-1/4 bottom-2 rounded-sm opacity-30" style={{ backgroundColor: accent }} />
        <div className="absolute inset-x-2 bottom-2 h-1/3 bg-black/60 border border-[#3a3a3a] flex flex-col items-center justify-center gap-0.5 py-1">
          <span className="font-display text-[10px] text-white leading-none">APE-TIDES</span>
          <span className="font-mono2 text-[8px]" style={{ color: accent }}>{label}</span>
        </div>
        <div className="absolute left-1 top-2 w-1/3 h-1/2 bg-white/10 blur-sm rounded-full" />
      </div>
    </div>
  );
}
